import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../api/useApi';
import { ApiError, type BriefingResponse } from '../api/client';
import { BriefingMarkdown } from '../components/BriefingMarkdown';
import { PackagePanel } from '../components/PackagePanel';
import {
  groupPackageLinks,
  headingSlug,
  isPackageSection,
  parseBriefing,
} from '../lib/briefing-md';
import { formatLongDateLocal, formatShortTime, parseLocalDate } from '../lib/format';

/**
 * Pulls the briefing date out of the document title (e.g.
 * `Review Concierge Briefing - 2026-08-20`) as a long local date label.
 * Returns `null` when the title has no date or the date is out of range
 * (e.g. `2026-99-99`), so a rolled-over date never renders as a chip.
 */
function briefingDateLabel(title: string | null): string | null {
  const ymd = title ? /\d{4}-\d{2}-\d{2}/.exec(title)?.[0] : undefined;
  if (!ymd) return null;
  const local = parseLocalDate(ymd);
  if (!local) return null;
  return formatLongDateLocal(local);
}

/**
 * The engineering review briefing — an org surface, the same for every
 * Almanac user. Authored daily by the review-concierge scheduled task and
 * merged into almanac-wiki; the API reads it from wiki HEAD. Rendered here
 * as digest-style sections split from the briefing markdown.
 */
export function BriefingPage() {
  const api = useApi();
  const [briefing, setBriefing] = useState<BriefingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // The open package lives in the URL (`/briefing/packages/:packageSlug`),
  // not local state, so package links are real links — middle-click, copy,
  // and direct open all land on the doc — and Back closes the panel.
  const { packageSlug } = useParams<{ packageSlug: string }>();
  const openPackage = packageSlug ?? null;
  const navigate = useNavigate();
  const setOpenPackage = useCallback(
    (slug: string | null) => {
      if (slug) {
        navigate(`/briefing/packages/${encodeURIComponent(slug)}`);
      } else {
        navigate('/briefing');
      }
    },
    [navigate],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getBriefing()
      .then(setBriefing)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setError('No briefing has been published yet. The first concierge run creates it.');
        } else if (err instanceof ApiError && err.status === 503) {
          setError('The briefing is unavailable in this environment.');
        } else {
          setError('Failed to load the briefing.');
        }
      })
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const bodyMd = briefing?.body_md;
  const parsed = useMemo(() => {
    if (bodyMd === undefined) return null;
    return parseBriefing(bodyMd);
  }, [bodyMd]);

  let content: ReactNode = null;
  if (loading) {
    content = (
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  } else if (error) {
    content = (
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        {error}
      </div>
    );
  } else if (briefing && parsed) {
    const dateLabel = briefingDateLabel(parsed.title);
    // Date and time both come from the same local Date so the pair can't
    // disagree around UTC midnight.
    const fetchedAt = new Date(briefing.fetched_at);
    let dateChip: ReactNode = null;
    if (dateLabel) {
      dateChip = (
        <span className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] font-medium text-foreground/70">
          {dateLabel}
        </span>
      );
    }
    let freshnessNote: ReactNode = null;
    if (parsed.freshness) {
      freshnessNote = (
        <details className="text-xs leading-relaxed text-muted-foreground">
          <summary className="w-fit cursor-pointer rounded-md font-medium text-foreground/70 select-none hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
            Coverage details
          </summary>
          <p className="mt-1.5">{parsed.freshness}</p>
        </details>
      );
    }
    // Packages become bordered, numbered cards in the main column; the
    // briefing's apparatus (the short version, review batches, coverage)
    // reads as a skimmable rail that scrolls independently on wide screens.
    const metaSections = parsed.sections.filter((section) => !isPackageSection(section.title));
    const packageSections = parsed.sections.filter((section) => isPackageSection(section.title));
    content = (
      <div className="space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Engineering features
          </h1>
          <p className="text-sm text-muted-foreground">
            Feature progress, open decisions, and what ships next across the product repos — built
            for engineering, open to everyone.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {dateChip}
            <span>
              Read from the wiki {formatLongDateLocal(fetchedAt)} at{' '}
              {formatShortTime(briefing.fetched_at)}
            </span>
          </div>
          {freshnessNote}
        </header>
        {parsed.preamble ? (
          <BriefingMarkdown body={parsed.preamble} onOpenPackage={setOpenPackage} />
        ) : null}
        <div className="items-start gap-10 lg:grid lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
          <aside className="space-y-10 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pb-8 lg:pr-2">
            {metaSections.map((section, index) => (
              <section
                key={`${index}-${section.title}`}
                id={headingSlug(section.title)}
                aria-label={section.title}
                className="scroll-mt-24 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <span aria-hidden className="h-4 w-[3px] flex-none rounded-full bg-primary" />
                  <h2 className="font-display text-lg font-semibold text-foreground">
                    {section.title}
                  </h2>
                  <span aria-hidden className="h-px flex-1 bg-border" />
                </div>
                <BriefingMarkdown body={section.body} onOpenPackage={setOpenPackage} />
              </section>
            ))}
          </aside>
          <div className="mt-10 space-y-6 lg:mt-0">
            {packageSections.map((section, index) => (
              <section
                key={`${index}-${section.title}`}
                id={headingSlug(section.title)}
                aria-label={section.title}
                className="scroll-mt-24 rounded-xl border border-border bg-card p-5 sm:p-6"
              >
                <div className="mb-4 flex items-center gap-3 border-b border-border/60 pb-3">
                  <span
                    aria-hidden
                    className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10 font-display text-xs font-semibold text-primary"
                  >
                    {index + 1}
                  </span>
                  <h2 className="font-display text-lg font-semibold text-foreground">
                    {section.title}
                  </h2>
                </div>
                <BriefingMarkdown
                  body={groupPackageLinks(section.body)}
                  onOpenPackage={setOpenPackage}
                />
              </section>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // The panel fetches its own doc, so it renders whenever the URL names a
  // package — a direct open works even while the briefing loads or errors.
  const panel = openPackage ? (
    <PackagePanel
      slug={openPackage}
      onOpenPackage={setOpenPackage}
      onClose={() => setOpenPackage(null)}
    />
  ) : null;

  if (briefing && parsed) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {content}
        {panel}
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold text-foreground">
          Engineering features
        </h1>
      </div>
      {content}
      {panel}
    </main>
  );
}
